import { z } from "zod";

import { HousingDetailsSchema } from "@/.zenstack/zod/models/HousingDetails.schema";

/**
 * Error-only schema for Property Info form (blocking validation)
 *
 * Contains only required field validation.
 * This validation blocks form submission.
 */
export const PropertyInfoErrorSchema = z
  .object({
    // Make required fields explicit for form validation
    bedrooms: z
      .string()
      .min(1, "Number of bedrooms is required")
      .refine(
        (val) =>
          val === "Studio" ||
          val === "6+" ||
          (!isNaN(Number(val)) && Number(val) > 0),
        "Bedrooms must be Studio, 6+, or a valid number greater than 0",
      ),
    bathrooms: z
      .string()
      .min(1, "Number of bathrooms is required")
      .refine(
        (val) => !isNaN(Number(val)) && Number(val) > 0,
        "Bathrooms must be a valid number greater than 0",
      ),
    // Square footage is optional - landlords may omit it to avoid legal disputes
    // Check for empty string FIRST before coercion to prevent "" -> 0 conversion
    squareFootage: z
      .union([
        z.literal("").transform(() => undefined),
        z.coerce
          .number({ message: "Square footage must be a valid number" })
          .positive("Square footage must be greater than 0"),
      ])
      .optional(),
  })
  .merge(
    // Merge with other optional fields from HousingDetailsSchema
    HousingDetailsSchema.pick({
      neighborhood: true,
      amenities: true,
    }),
  );

/**
 * Warning-only schema for Property Info form (non-blocking recommendations)
 *
 * Contains UX recommendations that don't block submission.
 * These are shown immediately to guide users toward best practices.
 */
export const PropertyInfoWarningSchema = z
  .object({
    bedrooms: z.string(),
    bathrooms: z.string(),
    // Check for empty string FIRST before coercion to prevent "" -> 0 conversion
    squareFootage: z
      .union([z.literal("").transform(() => undefined), z.coerce.number()])
      .optional(),
  })
  .merge(
    HousingDetailsSchema.pick({
      neighborhood: true,
      amenities: true,
    }),
  )
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Flag unusually low square footage
    if (data.squareFootage && data.squareFootage < 300) {
      ctx.addIssue({
        code: "custom",
        message: "Square footage seems low - please verify this is accurate.",
        severity: "warning",
        path: ["squareFootage"],
      });
    }
  })
  .superRefine((data, ctx) => {
    // CLIENT WARNING: Flag unusually high square footage
    if (data.squareFootage && data.squareFootage > 10000) {
      ctx.addIssue({
        code: "custom",
        message: "Square footage seems high - please verify this is accurate.",
        severity: "warning",
        path: ["squareFootage"],
      });
    }
  });
// FEATURE: correlate square footage with bedrooms and bathrooms

export type PropertyInfoFormData = z.infer<typeof PropertyInfoErrorSchema>;
